import moment from "moment";
import { useEffect, useRef, useState } from "react";
import {
  getFileDownloadUrl,
  getFileMetadata,
} from "../../../api/firebase/firestoreService";

interface UseDenialLetterUploadScreenshotReturn {
  screenshotUrl: string;
  loading: boolean;
  error: string | null;
  metadata: {
    timeCreated: string | null;
    updated: string | null;
  } | null;
  formattedTime: string;
}

export const useDenialLetterUploadScreenshot = (
  identifier: string | null,
): UseDenialLetterUploadScreenshotReturn => {
  const [screenshotUrl, setScreenshotUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<{
    timeCreated: string | null;
    updated: string | null;
  } | null>(null);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    isMountedRef.current = true;

    const fetchScreenshot = async () => {
      if (!identifier) {
        if (isMountedRef.current) {
          setScreenshotUrl("");
          setLoading(false);
          setError(null);
          setMetadata(null);
        }
        return;
      }

      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const filePath = `onco_emr/${identifier}/onco_emr_denial_letter_upload_screenshot/denial_letter_upload.png`;
        const [downloadUrl, fileMetadata] = await Promise.all([
          getFileDownloadUrl(filePath),
          getFileMetadata(filePath),
        ]);

        if (isMountedRef.current) {
          if (downloadUrl) {
            setScreenshotUrl(downloadUrl);
            setMetadata(fileMetadata);
            setError(null);
          } else {
            setError("Failed to fetch screenshot URL");
            setScreenshotUrl("");
            setMetadata(null);
          }
        }
      } catch (err) {
        if (isMountedRef.current) {
          console.error("Error fetching denial letter upload screenshot:", err);
          setError("Error fetching screenshot");
          setScreenshotUrl("");
          setMetadata(null);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchScreenshot();

    return () => {
      isMountedRef.current = false;
    };
  }, [identifier]);

  const formattedTime = metadata?.updated
    ? moment
        .utc(metadata.timeCreated)
        .add(5, "hours")
        .add(30, "minutes")
        .format("MMM DD, YYYY h:mm A")
    : "";

  return { screenshotUrl, loading, error, metadata, formattedTime };
};
